import type { TagTone } from '@mybusinessmate/ui-tokens';
import { cx } from '../../lib/cx';

/** `website` / `marketing` / `automation` are the project domains' own colours (Figma C03.1 cards). */
export type ProgressTone = 'brand' | 'dark' | 'success' | 'website' | 'marketing' | 'automation' | TagTone;

// Full class names must appear literally for Tailwind to generate them.
const FILL: Record<ProgressTone, string> = {
  brand: 'bg-brand-primary',
  dark: 'bg-action-primary',
  /** Figma A05.7: a task that is done (100%) on the My work board. */
  success: 'bg-feedback-success',
  website: 'bg-accent-website',
  marketing: 'bg-accent-marketing',
  automation: 'bg-accent-automation',
  blue: 'bg-tag-blue-solid-bg',
  pink: 'bg-tag-pink-solid-bg',
  indigo: 'bg-tag-indigo-solid-bg',
  amber: 'bg-tag-amber-solid-bg',
  red: 'bg-tag-red-solid-bg',
  green: 'bg-tag-green-solid-bg',
  grey: 'bg-tag-grey-solid-bg',
};

export interface ProgressBarProps {
  /** 0-100. Out-of-range values are clamped. */
  value: number;
  tone?: ProgressTone;
  /** `slim` is the 4px track used in table rows (Figma A03.1 quota, A04 workload); the default is 6px; `thick` is the 8px bar of a client's "working on it" stage (Figma C03.10a). */
  size?: 'default' | 'slim' | 'thick';
  /** `strong` is the darker track for a bar that sits on a grey row (Figma P01 upload row). */
  track?: 'default' | 'strong';
  /** Accessible name, e.g. the project name. */
  label: string;
  className?: string;
}

/** Figma "Progress bar" (495:33753): a slim track with a rounded fill. One component, driven by `value`. */
export function ProgressBar({
  value,
  tone = 'brand',
  size = 'default',
  track = 'default',
  label,
  className,
}: ProgressBarProps) {
  const clamped = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className={cx(
        'w-full overflow-hidden rounded-full',
        size === 'slim' ? 'h-(--mbm-space-1)' : size === 'thick' ? 'h-(--mbm-space-2)' : 'h-(--mbm-space-1-5)',
        track === 'strong' ? 'bg-border-default' : size === 'slim' ? 'bg-surface-field' : 'bg-surface-tint',
        className,
      )}
    >
      <div className={cx('h-full rounded-full', FILL[tone])} style={{ width: `${clamped}%` }} />
    </div>
  );
}
